import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  Modal,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
  List,
  Avatar,
  Typography,
  Empty,
  Switch,
  Divider,
  Select,
  Spin,
  Dropdown,
  Drawer
} from 'antd';
import type { MenuProps } from 'antd';
import {
  FolderOutlined,
  DeleteOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  FileTextOutlined,
  ShareAltOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sharedService, SharedFolder, SharedFolderPermissions } from '../../services/literature/sharedService';
import {
  literatureService,
  Literature,
  literatureHasPdfAttachment,
  literatureHasPdfIncludingSnapshot
} from '../../services/literature/literatureService';
import { categoryService, type Category } from '../../services/literature/categoryService';
import LiteratureInspectorPanel from '../../components/literature/LiteratureInspectorPanel';
import { LiteratureLimsShell } from '../../components/literature/LiteratureLimsShell';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { getMembers } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { User } from '../../types';
import type { ColumnsType } from 'antd/es/table';
import '../../styles/literature-stitch.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const ALL_FOLDERS_VALUE = '';

type SharedFolderRef = { folderId: string; folderName: string };

type DisplayLiterature = Literature & {
  _sharedFolderHint?: string;
  /** 「全部文件夹」合并视图：文献所在文件夹（单选视图不填） */
  _sharedFolderRefs?: SharedFolderRef[];
  /** 共享接口合并的 SharedLiterature.snapshot（可能含同步时的 pdfUrl） */
  snapshot?: { pdfUrl?: string | null; title?: string } | null;
};

function formatLiteratureRelativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return d.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

function literatureSnippet(record: Literature): string {
  const raw = (record.abstract || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return raw.slice(0, 220);
}

function readStatusPillClass(status: string | undefined): string {
  switch (status) {
    case '未读':
      return 'lit-lib-pill lit-lib-pill--muted';
    case '阅读中':
      return 'lit-lib-pill lit-lib-pill--reading';
    case '已读':
      return 'lit-lib-pill lit-lib-pill--clinical';
    case '精读':
      return 'lit-lib-pill lit-lib-pill--protocol';
    default:
      return 'lit-lib-pill lit-lib-pill--muted';
  }
}

const SharedFolders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [folders, setFolders] = useState<SharedFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [displayLiteratures, setDisplayLiteratures] = useState<DisplayLiterature[]>([]);
  const [litLoading, setLitLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [inspectorSelectedId, setInspectorSelectedId] = useState<string | null>(null);
  const [inspectorDrawerOpen, setInspectorDrawerOpen] = useState(false);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [addLiteratureModalVisible, setAddLiteratureModalVisible] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<SharedFolder | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderDescription, setFolderDescription] = useState('');
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [invitingUsers, setInvitingUsers] = useState(false);
  const [allLiteratures, setAllLiteratures] = useState<Literature[]>([]);
  const [selectedLiteratureIds, setSelectedLiteratureIds] = useState<string[]>([]);
  const [loadingLiteratures, setLoadingLiteratures] = useState(false);
  const [syncNotes, setSyncNotes] = useState(false);
  const [permissions, setPermissions] = useState<SharedFolderPermissions>({
    canEditNotes: false,
    canEditLiterature: false,
    canAddLiterature: false,
    canRemoveLiterature: false,
    canViewUsers: false
  });

  const activeFolder = useMemo(
    () => (selectedFolderId ? folders.find((f) => f._id === selectedFolderId) ?? null : null),
    [folders, selectedFolderId]
  );

  const setFolderFilter = useCallback(
    (id: string | null) => {
      setSelectedFolderId(id);
      if (id) setSearchParams({ folder: id }, { replace: true });
      else setSearchParams({}, { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadFolderLiteratures = useCallback(async () => {
    if (folders.length === 0) {
      setDisplayLiteratures([]);
      return;
    }
    setLitLoading(true);
    try {
      if (!selectedFolderId) {
        const results = await Promise.all(folders.map((f) => sharedService.getFolderLiteratures(f._id)));
        const map = new Map<string, { lit: Literature; refs: SharedFolderRef[] }>();
        results.forEach((data, idx) => {
          const folder = folders[idx];
          const ref: SharedFolderRef = { folderId: folder._id, folderName: folder.name };
          for (const lit of data.literatures as Literature[]) {
            const cur = map.get(lit._id);
            if (cur) {
              if (!cur.refs.some((r) => r.folderId === ref.folderId)) cur.refs.push(ref);
            } else {
              map.set(lit._id, { lit, refs: [ref] });
            }
          }
        });
        setDisplayLiteratures(
          Array.from(map.values()).map(({ lit, refs }) => {
            const names = refs.map((r) => r.folderName);
            return {
              ...lit,
              _sharedFolderRefs: refs,
              _sharedFolderHint: names.length > 1 ? names.join('、') : names[0]
            };
          })
        );
      } else {
        const data = await sharedService.getFolderLiteratures(selectedFolderId);
        setDisplayLiteratures((data.literatures as Literature[]).map((lit) => ({ ...lit })));
      }
    } catch (error: any) {
      message.error(error.message || '加载共享文献失败');
      setDisplayLiteratures([]);
    } finally {
      setLitLoading(false);
    }
  }, [folders, selectedFolderId]);

  useEffect(() => {
    void loadFolderLiteratures();
  }, [loadFolderLiteratures]);

  useEffect(() => {
    if (folders.length === 0) return;
    const fp = searchParams.get('folder');
    if (!fp) {
      setSelectedFolderId(null);
      return;
    }
    if (!folders.some((x) => x._id === fp)) {
      setSearchParams({}, { replace: true });
      setSelectedFolderId(null);
      return;
    }
    setSelectedFolderId(fp);
  }, [searchParams, folders, setSearchParams]);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const [myFolders, sharedWithMeFolders] = await Promise.all([
        sharedService.getMyFolders(),
        sharedService.getSharedWithMeFolders()
      ]);

      const allFolders = [
        ...myFolders.map((f) => ({ ...f, isOwner: true })),
        ...sharedWithMeFolders.map((f) => ({ ...f, isOwner: false }))
      ];

      setFolders(allFolders);
    } catch (error: any) {
      message.error(error.message || '获取共享文件夹列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchFolders();
  }, []);

  const refreshCategories = useCallback(async () => {
    try {
      const list = await categoryService.getList();
      setCategories(Array.isArray(list) ? list : []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    void refreshCategories();
    const onCat = () => void refreshCategories();
    window.addEventListener('categoryUpdated', onCat);
    return () => window.removeEventListener('categoryUpdated', onCat);
  }, [refreshCategories]);

  useEffect(() => {
    setInspectorSelectedId(null);
    setInspectorDrawerOpen(false);
  }, [selectedFolderId]);

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      message.warning('请输入文件夹名称');
      return;
    }

    try {
      await sharedService.createFolder({
        name: folderName.trim(),
        description: folderDescription.trim()
      });
      message.success('创建成功');
      setCreateModalVisible(false);
      setFolderName('');
      setFolderDescription('');
      fetchFolders();
    } catch (error: any) {
      message.error(error.message || '创建失败');
    }
  };

  const handleDeleteFolder = async (id: string) => {
    try {
      await sharedService.deleteFolder(id);
      message.success('删除成功');
      if (selectedFolderId === id) setFolderFilter(null);
      fetchFolders();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const currentUserId = String(currentUser?._id || currentUser?.id || '');

  const inviteableMembers = useMemo(() => {
    if (!selectedFolder) return [];
    const occupiedIds = new Set<string>([
      String(selectedFolder.owner?._id || ''),
      currentUserId,
      ...selectedFolder.sharedUsers.map((su) => String(su.user?._id || ''))
    ].filter(Boolean));

    return teamMembers.filter((member) => {
      const id = String(member._id || '');
      return id && !occupiedIds.has(id);
    });
  }, [teamMembers, selectedFolder, currentUserId]);

  const openInviteUserModal = async (folder: SharedFolder) => {
    setSelectedFolder(folder);
    setSelectedMemberIds([]);
    setAddUserModalVisible(true);
    setMembersLoading(true);
    try {
      const response = await getMembers();
      if (response.success) {
        setTeamMembers(response.data || []);
      } else {
        message.error(response.error || '获取团队成员失败');
        setTeamMembers([]);
      }
    } catch (error: any) {
      message.error(error.message || '获取团队成员失败');
      setTeamMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (selectedMemberIds.length === 0) {
      message.warning('请选择要邀请的成员');
      return;
    }

    if (!selectedFolder) return;

    setInvitingUsers(true);
    try {
      const result = await sharedService.inviteUsers(selectedFolder._id, selectedMemberIds);
      message.success(result.message || '邀请已发送，对方可在顶部通知或审批管理 → 待办事项中处理');
      setAddUserModalVisible(false);
      setSelectedMemberIds([]);
      fetchFolders();
    } catch (error: any) {
      message.error(error.message || '发送邀请失败');
    } finally {
      setInvitingUsers(false);
    }
  };

  const handleRemoveUser = async (folderId: string, userId: string) => {
    try {
      await sharedService.removeSharedUser(folderId, userId);
      message.success('移除共享用户成功');
      fetchFolders();
    } catch (error: any) {
      message.error(error.message || '移除共享用户失败');
    }
  };

  const handleOpenAddLiterature = async (folder: SharedFolder) => {
    setSelectedFolder(folder);
    setLoadingLiteratures(true);
    try {
      const [allLiteraturesResult, folderLiteraturesResult] = await Promise.all([
        literatureService.getList({ page: 1, limit: 1000 }),
        sharedService.getFolderLiteratures(folder._id)
      ]);

      setAllLiteratures(allLiteraturesResult.literatures);

      const existingLiteratureIds = folderLiteraturesResult.literatures.map((lit: Literature) => lit._id);
      setSelectedLiteratureIds(existingLiteratureIds);

      setAddLiteratureModalVisible(true);
    } catch (error: any) {
      message.error('获取文献列表失败');
    } finally {
      setLoadingLiteratures(false);
    }
  };

  const handleAddLiteratures = async () => {
    if (!selectedFolder || selectedLiteratureIds.length === 0) {
      message.warning('请选择要添加的文献');
      return;
    }

    try {
      await sharedService.addLiteraturesToFolder(selectedFolder._id, selectedLiteratureIds, syncNotes);
      message.success(syncNotes ? '添加文献成功，笔记内容已同步' : '添加文献成功');
      setAddLiteratureModalVisible(false);
      setSelectedLiteratureIds([]);
      setSyncNotes(false);
      void fetchFolders();
      void loadFolderLiteratures();
    } catch (error: any) {
      message.error(error.message || '添加文献失败');
    }
  };

  const handleOpenPermissions = (folder: SharedFolder) => {
    setSelectedFolder(folder);
    const p = folder.permissions;
    setPermissions({
      canEditNotes: p?.canEditNotes ?? false,
      canEditLiterature: p?.canEditLiterature ?? false,
      canAddLiterature: p?.canAddLiterature ?? false,
      canRemoveLiterature: p?.canRemoveLiterature ?? false,
      canViewUsers: p?.canViewUsers ?? false
    });
    setPermissionsModalVisible(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedFolder) return;

    try {
      await sharedService.updateFolder(selectedFolder._id, { permissions });
      message.success('权限设置已保存');
      setPermissionsModalVisible(false);
      fetchFolders();
    } catch (error: any) {
      message.error(error.message || '保存权限设置失败');
    }
  };

  const canRemoveInFolder = useCallback((folderId: string) => {
    const folder = folders.find((f) => f._id === folderId);
    if (!folder) return false;
    if (folder.isOwner) return true;
    return !!folder.permissions?.canRemoveLiterature;
  }, [folders]);

  const removeLiteratureFromFolder = async (folderId: string, literatureId: string) => {
    await sharedService.removeLiteratureFromFolder(folderId, literatureId);
  };

  const handleRemoveLiteratureSingleFolder = async (literatureId: string, folderId: string) => {
    try {
      await removeLiteratureFromFolder(folderId, literatureId);
      message.success('已移除');
      void loadFolderLiteratures();
      void fetchFolders();
    } catch (error: any) {
      message.error(error.message || '移除文献失败');
    }
  };

  const handleRemoveLiteratureAllRefs = async (record: DisplayLiterature) => {
    const refs = record._sharedFolderRefs?.filter((r) => canRemoveInFolder(r.folderId)) ?? [];
    if (refs.length === 0) {
      message.warning('没有可移除的文件夹（需为所有者或具备「移除文献」权限）');
      return;
    }
    try {
      for (const r of refs) {
        await removeLiteratureFromFolder(r.folderId, record._id);
      }
      message.success(refs.length > 1 ? `已从 ${refs.length} 个文件夹中移除` : '已移除');
      void loadFolderLiteratures();
      void fetchFolders();
    } catch (error: any) {
      message.error(error.message || '移除文献失败');
      void loadFolderLiteratures();
    }
  };

  const openInspector = (literatureId: string) => {
    setInspectorSelectedId(literatureId);
    setInspectorDrawerOpen(true);
  };

  const navigateToSharedLiteraturePdf = (record: DisplayLiterature) => {
    const snapUrl = record.snapshot?.pdfUrl?.trim();
    if (literatureHasPdfAttachment(record)) {
      navigate(`/literature/${record._id}/view`);
    } else if (snapUrl) {
      navigate(`/literature/${record._id}/view?pdf=${encodeURIComponent(snapUrl)}`);
    }
  };

  const buildCardActionMenu = (record: DisplayLiterature): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'inspect',
        label: '打开检视',
        icon: <EyeOutlined />,
        onClick: () => openInspector(record._id)
      }
    ];
    if (literatureHasPdfIncludingSnapshot(record)) {
      items.push({
        key: 'pdf',
        label: '查看 PDF',
        icon: <EyeOutlined />,
        onClick: () => navigateToSharedLiteraturePdf(record)
      });
    }

    const removableFolderIds = selectedFolderId
      ? activeFolder && canRemoveInFolder(selectedFolderId)
        ? [selectedFolderId]
        : []
      : (record._sharedFolderRefs ?? []).filter((r) => canRemoveInFolder(r.folderId)).map((r) => r.folderId);

    if (removableFolderIds.length === 1) {
      const fid = removableFolderIds[0];
      items.push({ type: 'divider' });
      items.push({
        key: 'remove',
        danger: true,
        label: '从文件夹移除',
        icon: <DeleteOutlined />,
        onClick: () => {
          Modal.confirm({
            title: '确定要从该共享文件夹中移除此文献吗？',
            okText: '确定',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => void handleRemoveLiteratureSingleFolder(record._id, fid)
          });
        }
      });
    } else if (removableFolderIds.length > 1) {
      const names = (record._sharedFolderRefs ?? [])
        .filter((r) => removableFolderIds.includes(r.folderId))
        .map((r) => r.folderName);
      items.push({ type: 'divider' });
      items.push({
        key: 'remove-all',
        danger: true,
        label: '从相关文件夹移除',
        icon: <DeleteOutlined />,
        onClick: () => {
          Modal.confirm({
            title: '从多个共享文件夹中移除此文献？',
            content: `将在您有权限的 ${removableFolderIds.length} 个文件夹中移除：${names.join('、')}`,
            okText: '全部移除',
            cancelText: '取消',
            okType: 'danger',
            onOk: () => void handleRemoveLiteratureAllRefs(record)
          });
        }
      });
    }

    return items;
  };

  const literatureColumns: ColumnsType<Literature> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: '作者',
      dataIndex: 'authors',
      key: 'authors',
      render: (authors: string[]) => authors?.slice(0, 2).join(', ') || '-'
    },
    {
      title: '年份',
      dataIndex: 'year',
      key: 'year',
      width: 80
    }
  ];

  const folderSelectOptions = useMemo(
    () => [
      { value: ALL_FOLDERS_VALUE, label: '全部文件夹' },
      ...folders.map((f) => ({
        value: f._id,
        label: `${f.name}${f.isOwner ? '（我的）' : '（共享给我）'}`
      }))
    ],
    [folders]
  );

  return (
    <>
    <LiteratureLimsShell
      crumbs={[
        { label: '首页', path: '/dashboard' },
        { label: '文献管理', path: '/literature' },
        { label: '共享文献' }
      ]}
      title="共享文献"
      subtitle="按文件夹筛选并浏览共享空间中的文献；创建文件夹、邀请成员与权限仍在下方管理。"
      headActions={
        <button type="button" className="mm-btn-primary" onClick={() => setCreateModalVisible(true)}>
          <LimsNavIcon name="folder_shared" sizePx={20} />
          创建共享文件夹
        </button>
      }
    >
      {loading ? (
        <div className="mm-table-shell" style={{ textAlign: 'center', padding: 48 }}>
          <Text type="secondary">加载中...</Text>
        </div>
      ) : folders.length === 0 ? (
        <div className="mm-table-shell" style={{ padding: 48 }}>
          <Empty description="还没有共享文件夹，创建一个开始共享文献吧" />
        </div>
      ) : (
        <div className="lit-ant-table-wrap lit-lib-list-bg" style={{ padding: '8px 0 24px' }}>
          <div className="mm-table-shell lit-mm-table-shell--stitch" style={{ boxShadow: 'none', border: 'none', background: 'transparent' }}>
            <div className="mm-table-toolbar" style={{ marginBottom: 12 }}>
              <div className="lit-toolbar-inner">
                <div className="lit-toolbar-filters" style={{ width: '100%', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--st-on-surface, #191c1d)', marginRight: 8 }}>文件夹</span>
                  <Select
                    className="lit-toolbar-select"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: isMobile ? 200 : 280, maxWidth: '100%' }}
                    value={selectedFolderId ?? ALL_FOLDERS_VALUE}
                    options={folderSelectOptions}
                    onChange={(v) => setFolderFilter(v === ALL_FOLDERS_VALUE ? null : String(v))}
                  />
                </div>
              </div>
            </div>

            {activeFolder ? (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: 'var(--st-surface-low, #f3f4f5)',
                  borderRadius: 'var(--mm-radius-xl, 16px)',
                  border: '1px solid rgba(194, 199, 209, 0.35)'
                }}
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Space wrap align="center">
                    <Text strong style={{ fontSize: 15 }}>
                      {activeFolder.name}
                    </Text>
                    {activeFolder.isOwner ? <Tag color="blue">我的</Tag> : <Tag color="green">共享给我的</Tag>}
                  </Space>
                  {activeFolder.description ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {activeFolder.description}
                    </Text>
                  ) : null}
                  <Space wrap>
                    <Tag>所有者: {activeFolder.owner.username}</Tag>
                    <Tag>共享用户: {activeFolder.sharedUsers.length} 人</Tag>
                    <Tag>文献: {displayLiteratures.length} 篇</Tag>
                  </Space>
                  {activeFolder.isOwner ? (
                    <Space wrap>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<UserAddOutlined />}
                        onClick={() => void openInviteUserModal(activeFolder)}
                      >
                        邀请用户
                      </Button>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => void handleOpenAddLiterature(activeFolder)}
                      >
                        添加文献
                      </Button>
                      <Button
                        type="default"
                        className="lit-stitch-btn-secondary"
                        size="small"
                        icon={<SettingOutlined />}
                        onClick={() => handleOpenPermissions(activeFolder)}
                      >
                        权限设置
                      </Button>
                      <Popconfirm
                        title="确定要删除这个共享文件夹吗？"
                        onConfirm={() => void handleDeleteFolder(activeFolder._id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button type="default" danger size="small" icon={<DeleteOutlined />}>
                          删除文件夹
                        </Button>
                      </Popconfirm>
                    </Space>
                  ) : null}
                  {!activeFolder.isOwner && activeFolder.sharedUsers.length > 0 ? (
                    <List
                      size="small"
                      header={<Text type="secondary">共享成员</Text>}
                      dataSource={activeFolder.sharedUsers}
                      renderItem={(user) => (
                        <List.Item style={{ padding: '4px 0' }}>
                          <List.Item.Meta
                            avatar={<Avatar icon={<ShareAltOutlined />} size="small" />}
                            title={user.user.username}
                            description={user.email}
                          />
                          {user.status === 'pending' ? (
                            <Tag color="orange" icon={<ClockCircleOutlined />}>
                              待同意
                            </Tag>
                          ) : (
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                              已加入
                            </Tag>
                          )}
                        </List.Item>
                      )}
                    />
                  ) : null}
                  {activeFolder.isOwner && activeFolder.sharedUsers.length > 0 ? (
                    <List
                      size="small"
                      header={<Text type="secondary">共享成员</Text>}
                      dataSource={activeFolder.sharedUsers}
                      renderItem={(user) => (
                        <List.Item
                          style={{ padding: '4px 0' }}
                          actions={[
                            user.status === 'pending' ? (
                              <Tag color="orange" icon={<ClockCircleOutlined />}>
                                待同意
                              </Tag>
                            ) : (
                              <Tag color="green" icon={<CheckCircleOutlined />}>
                                已加入
                              </Tag>
                            ),
                            user.status === 'joined' ? (
                              <Button
                                type="link"
                                danger
                                size="small"
                                icon={<UserDeleteOutlined />}
                                onClick={() => void handleRemoveUser(activeFolder._id, user.user._id)}
                              >
                                移除
                              </Button>
                            ) : null
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<Avatar icon={<ShareAltOutlined />} size="small" />}
                            title={user.user.username}
                            description={user.email}
                          />
                        </List.Item>
                      )}
                    />
                  ) : null}
                </Space>
              </div>
            ) : (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--mm-on-variant, #5c6370)' }}>
                当前为「全部文件夹」：同一篇在多个文件夹中会合并为一条，标签展示来源。可在卡片菜单中「从文件夹移除」或「从相关文件夹移除」（将仅在您有权限的文件夹中执行）。
              </p>
            )}

            <Spin spinning={litLoading}>
              <div className="lit-lib-card-grid">
                {!litLoading && displayLiteratures.length === 0 ? (
                  <p style={{ gridColumn: '1 / -1', margin: 0, color: 'var(--mm-on-variant, #5c6370)' }}>
                    该范围下暂无文献。可在选择文件夹后使用「添加文献」。
                  </p>
                ) : null}
                {displayLiteratures.map((record) => {
                  const authorLine = record.authors?.slice(0, 2).join(', ') || '—';
                  const journalLine = record.journal || '—';
                  const snippet = literatureSnippet(record);
                  return (
                    <article
                      key={record._id + (record._sharedFolderHint || '')}
                      role="button"
                      tabIndex={0}
                      className={`lit-lib-card${
                        inspectorDrawerOpen && inspectorSelectedId === record._id ? ' lit-lib-card--selected' : ''
                      }`}
                      onClick={(e) => {
                        const el = e.target as HTMLElement;
                        if (el.closest('.ant-dropdown-trigger')) return;
                        if (el.closest('.lit-lib-card__actions')) return;
                        if (el.closest('button')) return;
                        openInspector(record._id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openInspector(record._id);
                        }
                      }}
                    >
                      <div className="lit-lib-card__top">
                        <div className="lit-lib-card__top-left">
                          {record._sharedFolderHint ? (
                            <Tag color="geekblue">{record._sharedFolderHint}</Tag>
                          ) : null}
                          <span className={readStatusPillClass(record.readStatus)}>{record.readStatus || '未读'}</span>
                        </div>
                        <div className="lit-lib-card__top-right">
                          <span className="lit-lib-card__time">{formatLiteratureRelativeTime(record.updatedAt)}</span>
                          <Dropdown
                            menu={{ items: buildCardActionMenu(record) }}
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button type="text" size="small" icon={<MoreOutlined />} title="更多" onClick={(ev) => ev.stopPropagation()} />
                          </Dropdown>
                        </div>
                      </div>
                      <h3 className="lit-lib-card__title">{record.title}</h3>
                      {snippet ? (
                        <p className="lit-lib-card__snippet">{snippet}</p>
                      ) : (
                        <p className="lit-lib-card__snippet">暂无摘要</p>
                      )}
                      <div className="lit-lib-card__meta">
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="person" sizePx={16} />
                          {authorLine}
                        </span>
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="menu_book" sizePx={16} />
                          {journalLine}
                          {record.year != null ? ` · ${record.year}` : ''}
                        </span>
                      </div>
                      {literatureHasPdfIncludingSnapshot(record) ? (
                        <div className="lit-lib-card__actions" onClick={(ev) => ev.stopPropagation()}>
                          <Button
                            type="default"
                            size="small"
                            className="lit-stitch-btn-secondary lit-lib-card__pdf-btn"
                            icon={<EyeOutlined />}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              navigateToSharedLiteraturePdf(record);
                            }}
                          >
                            查看 PDF
                          </Button>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </Spin>
          </div>
        </div>
      )}

      <Modal
        title="创建共享文件夹"
        open={createModalVisible}
        onOk={handleCreateFolder}
        onCancel={() => {
          setCreateModalVisible(false);
          setFolderName('');
          setFolderDescription('');
        }}
        okText="创建"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>文件夹名称</Text>
          <Input
            placeholder="请输入文件夹名称"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            style={{ marginTop: 8 }}
          />
        </div>
        <div>
          <Text strong>描述（可选）</Text>
          <TextArea
            placeholder="请输入描述"
            value={folderDescription}
            onChange={(e) => setFolderDescription(e.target.value)}
            rows={3}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      <Modal
        title="邀请团队成员"
        open={addUserModalVisible}
        onOk={() => void handleAddUser()}
        onCancel={() => {
          setAddUserModalVisible(false);
          setSelectedMemberIds([]);
        }}
        okText="发送邀请"
        cancelText="取消"
        confirmLoading={invitingUsers}
        okButtonProps={{ disabled: selectedMemberIds.length === 0 || membersLoading }}
      >
        <Spin spinning={membersLoading}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">从本团队成员中多选，批量发送共享邀请</Text>
          </div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="请选择要邀请的成员"
            style={{ width: '100%' }}
            value={selectedMemberIds}
            onChange={(ids) => setSelectedMemberIds(ids.map(String))}
            optionFilterProp="label"
            options={inviteableMembers.map((member) => {
              const id = String(member._id || '');
              const labelParts = [member.name || member.username];
              if (member.studentId) labelParts.push(member.studentId);
              return {
                value: id,
                label: labelParts.join(' · ')
              };
            })}
            notFoundContent={membersLoading ? '加载中...' : '暂无可邀请成员'}
            maxTagCount="responsive"
          />
          {selectedMemberIds.length > 0 ? (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              已选 {selectedMemberIds.length} 人
            </Text>
          ) : null}
        </Spin>
      </Modal>

      <Modal
        title={`添加文献到"${selectedFolder?.name}"`}
        open={addLiteratureModalVisible}
        onOk={handleAddLiteratures}
        onCancel={() => {
          setAddLiteratureModalVisible(false);
          setSelectedLiteratureIds([]);
          setSyncNotes(false);
        }}
        okText="添加"
        cancelText="取消"
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Switch checked={syncNotes} onChange={setSyncNotes} />
            <Text>同步文献信息和笔记内容</Text>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 26 }}>
            选中后，将同步文献的完整信息（标题、作者、摘要等）和笔记内容到共享文件夹
          </Text>
        </div>
        <Table
          columns={literatureColumns}
          dataSource={allLiteratures}
          rowKey="_id"
          loading={loadingLiteratures}
          rowSelection={{
            selectedRowKeys: selectedLiteratureIds,
            onChange: (keys) => setSelectedLiteratureIds(keys as string[])
          }}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>

      <Modal
        title={`权限设置 - "${selectedFolder?.name}"`}
        open={permissionsModalVisible}
        onOk={handleSavePermissions}
        onCancel={() => {
          setPermissionsModalVisible(false);
        }}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={5}>协作者权限</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
              以下开关仅对「非所有者」的共享成员生效；您作为所有者不受限制。
            </Text>
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>编辑笔记</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员编辑本共享空间内文献的笔记内容
                  </Text>
                </div>
                <Switch
                  checked={permissions.canEditNotes}
                  onChange={(checked) => setPermissions({ ...permissions, canEditNotes: checked })}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>编辑文献信息</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员修改标题、作者、摘要等书目字段
                  </Text>
                </div>
                <Switch
                  checked={permissions.canEditLiterature}
                  onChange={(checked) => setPermissions({ ...permissions, canEditLiterature: checked })}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>添加文献</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员将文献库中的条目加入本文件夹
                  </Text>
                </div>
                <Switch
                  checked={permissions.canAddLiterature}
                  onChange={(checked) => setPermissions({ ...permissions, canAddLiterature: checked })}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>移除文献</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员从本文件夹中移除文献（不删除文献库中的原文）
                  </Text>
                </div>
                <Switch
                  checked={permissions.canRemoveLiterature}
                  onChange={(checked) => setPermissions({ ...permissions, canRemoveLiterature: checked })}
                />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <Text strong>查看成员列表</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    允许成员查看已邀请/已加入的其他共享用户
                  </Text>
                </div>
                <Switch
                  checked={permissions.canViewUsers}
                  onChange={(checked) => setPermissions({ ...permissions, canViewUsers: checked })}
                />
              </div>
            </div>
          </div>
        </Space>
      </Modal>
    </LiteratureLimsShell>

      <Drawer
        rootClassName="lit-inspector-drawer lit-inspector-drawer--stitch lit-inspector-drawer--no-header"
        title={null}
        closable={false}
        placement="right"
        width={typeof window !== 'undefined' ? (isMobile ? Math.min(440, window.innerWidth) : 450) : 450}
        open={inspectorDrawerOpen && !!inspectorSelectedId}
        onClose={() => setInspectorDrawerOpen(false)}
        destroyOnHidden={false}
        styles={{
          body: { padding: 0, background: '#f8f9fa' },
          header: { display: 'none' }
        }}
      >
        <LiteratureInspectorPanel
          literatureId={inspectorSelectedId}
          categories={categories}
          showSectionRail={false}
          onNavigate={(p) => {
            navigate(p);
            setInspectorDrawerOpen(false);
          }}
          onLiteratureUpdated={() => {
            void loadFolderLiteratures();
            void fetchFolders();
          }}
          onSelectRelated={(id) => {
            setInspectorSelectedId(id);
            setInspectorDrawerOpen(true);
          }}
          onDeleted={() => {
            setInspectorDrawerOpen(false);
            setInspectorSelectedId(null);
            void loadFolderLiteratures();
            void fetchFolders();
          }}
        />
      </Drawer>
    </>
  );
};

export default SharedFolders;
